'use client';

import { useEffect, useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Divider } from '@sola/ark-core/atoms/divider/divider.tsx';
import { Form, useFormMethods } from '@sola/ark-core/molecules/form/form.tsx';
import type { ModalProps } from '@sola/ark-core/molecules/modal/modal.tsx';
import {
  Sheet,
  SheetActions,
  SheetBody,
  SheetBodyHeader,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
} from '@sola/ark-core/molecules/sheet/sheet.tsx';
import { useCart } from '@sola/cart-sdk/store.tsx';
import type { CommitmentCartPaymentItem } from '@sola/my-sdk/commitments/hooks.ts';
import { useCommitmentPaymentsAdd } from '@sola/my-sdk/commitments/hooks.ts';
import type { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { isItemPayment } from '@sola/shared-utils/cart/cart-item-type-guards.ts';
import { useMakeAPaymentCommitments } from '@sola/supporter-sdk/hooks/commitments.ts';

import { useLocalAlerts } from '../../molecules/local-alerts/local-alerts-provider.tsx';
import { LocalAlerts } from '../../molecules/local-alerts/local-alerts.tsx';
import { MakeAPaymentSection } from '../../molecules/make-a-payment-section/make-a-payment-section.tsx';
import { createCommitmentPaymentAddErrorAlert } from '../../utils/alerts.tsx';

const getDefaultFormValues = (
  missedPayments: GraphQLCommitment[],
  amountDue: GraphQLCommitment[],
  payAheadCommitments: GraphQLCommitment[],
  commitmentCartItemsMap: Map<string, CommitmentCartPaymentItem>,
) => {
  // Check if there are any cart values for the current commitments, if there are we will use those as the default form values, if not we will use the amounts from the commitments.
  const hasCartValues = commitmentCartItemsMap.size > 0;

  return [...missedPayments, ...amountDue, ...payAheadCommitments].reduce(
    (acc, commitment) => {
      const commitmentId = commitment.internalId;
      if (!commitmentId) {
        return acc;
      }

      const cartValue = commitmentCartItemsMap.get(commitmentId)?.unitPrice;

      acc[commitmentId] =
        cartValue ??
        (hasCartValues
          ? null
          : (commitment.dueThisMonthAmount || 0) +
              (commitment.missedPaymentAmount || 0) || null);

      return acc;
    },
    {} as Record<string, number | null>,
  );
};

const MakeAPaymentSheetTotalAmount: React.FC<{
  totalAmount: number;
}> = ({ totalAmount }) => {
  const { t } = useTranslation(['my', 'core']);
  return (
    <SheetBodyHeader isSticky className="border flex flex-col gap-comfy-xl">
      <div className="flex flex-col gap-snug-lg">
        <span className="label-potent label-lg label-neutral-mild inline-block">
          {t('my:makeAPayment.total')}
        </span>
        <span className="label-xl label-potent label-neutral-amplified">
          {t('core:numbers.currencyCustom', {
            amount: totalAmount,
          })}
        </span>
      </div>

      <Divider purpose="neutral" />
    </SheetBodyHeader>
  );
};

export interface MakeAPaymentSheetProps {
  open?: ModalProps['open'];
  onOpenChange?: ModalProps['onOpenChange'];
}

export const MakeAPaymentSheet: React.FC<MakeAPaymentSheetProps> = ({
  open,
  onOpenChange,
}) => {
  const { t } = useTranslation(['my', 'core']);
  const { cart } = useCart('cart');

  const {
    amountDue,
    missedPaymentCommitments,
    amountDueCommitments,
    payAheadCommitments,
  } = useMakeAPaymentCommitments();

  // Create a Map of all current commitment IDs from make a payment items for fast lookup
  const currentMakeAPaymentCommitmentIds = new Map<string, GraphQLCommitment>(
    [
      ...missedPaymentCommitments,
      ...amountDueCommitments,
      ...payAheadCommitments,
    ].flatMap((commitment) =>
      commitment.internalId
        ? [[commitment.internalId, commitment] as const]
        : [],
    ),
  );

  // Build a map of commitment ID to cart item for all payment items in the cart that match the current MAP commitments
  const commitmentCartItemsMap = new Map<string, CommitmentCartPaymentItem>(
    (cart?.items ?? [])
      .filter(isItemPayment)
      .filter((item) =>
        currentMakeAPaymentCommitmentIds.has(item.payment.commitmentId),
      )
      .map((item) => [item.payment.commitmentId, item] as const),
  );

  const totalAmountInCart = commitmentCartItemsMap.size
    ? [...commitmentCartItemsMap.values()].reduce(
        (sum, item) => sum + (item.unitPrice || 0),
        0,
      )
    : 0;

  const defaultFormValues = getDefaultFormValues(
    missedPaymentCommitments,
    amountDueCommitments,
    payAheadCommitments,
    commitmentCartItemsMap,
  );

  const { addAlerts, clearAlerts } = useLocalAlerts();

  const formMethods = useFormMethods();
  const { mutateAsync, isRedirectingToCheckout } = useCommitmentPaymentsAdd();
  const [isPending, setIsPending] = useState<boolean>(false);
  const isBusy = isPending || isRedirectingToCheckout;

  const [totalAmount, setTotalAmount] = useState(0);
  useEffect(() => {
    clearAlerts();
    setTotalAmount(
      totalAmountInCart > 0 ? totalAmountInCart : (amountDue ?? 0),
    );
  }, [amountDue, totalAmountInCart, open]);

  const calculateTotalFromFormValues = (values: FieldValues): number => {
    return Object.values(values).reduce((sum, value) => {
      return (
        sum + (typeof value === 'number' && !Number.isNaN(value) ? value : 0)
      );
    }, 0);
  };

  const handleSubmit = async (data: FieldValues) => {
    setIsPending(true);
    try {
      clearAlerts();

      await mutateAsync({
        payments: data,
      });
    } catch {
      addAlerts(createCommitmentPaymentAddErrorAlert());
    }
    setIsPending(false);
  };

  const canSubmit = totalAmount > 0 || commitmentCartItemsMap.size > 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange} isBusy={isBusy}>
      <SheetContent title={t('makeAPayment.title')}>
        <SheetHeader>{t('makeAPayment.title')}</SheetHeader>
        <SheetBody>
          <MakeAPaymentSheetTotalAmount totalAmount={totalAmount} />

          <Form
            onChange={() => {
              setTotalAmount(
                calculateTotalFromFormValues(
                  formMethods.current?.getValues() || {},
                ),
              );
            }}
            onSubmit={handleSubmit}
            formMethods={formMethods}
            options={{
              mode: 'onTouched',
              defaultValues: defaultFormValues,
            }}
          >
            <div className="mt-comfy-xl flex flex-col gap-comfy-xl">
              <LocalAlerts scrollPosition="end" />
              <MakeAPaymentSection
                title={t('makeAPayment.missingPayment.title')}
                description={t('makeAPayment.missingPayment.subtitle')}
                commitments={missedPaymentCommitments}
              />

              <MakeAPaymentSection
                title={t('makeAPayment.paymentDue.title')}
                description={t('makeAPayment.paymentDue.subtitle')}
                commitments={amountDueCommitments}
              />

              <MakeAPaymentSection
                alertMessage={t('makeAPayment.payAhead.alertMessage')}
                title={t('makeAPayment.payAhead.title')}
                description={t('makeAPayment.payAhead.subtitle')}
                commitments={payAheadCommitments}
              />

              <SheetFooter>
                <SheetActions>
                  <SheetClose asChild>
                    <ButtonStyle
                      size="lg"
                      purpose="neutral"
                      variant="secondary"
                      fullWidth
                    >
                      <button type="button">{t('core:actions.cancel')}</button>
                    </ButtonStyle>
                  </SheetClose>

                  <ButtonStyle
                    fullWidth
                    isBusy={isBusy}
                    disabled={!canSubmit || isBusy}
                  >
                    <button type="submit">{t('core:actions.continue')}</button>
                  </ButtonStyle>
                </SheetActions>
              </SheetFooter>
            </div>
          </Form>
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
};
